import { Component, useEffect, type ErrorInfo, type ReactNode } from 'react';
import LogRocket from 'logrocket';
import { useCurrentUser } from '@/hooks/useCurrentUser';

const LOGROCKET_APP_ID = 'vk1pxz/signal-ce5ru';

// Only record on real (built) deployments, not local dev. We key on Vite's MODE
// rather than import.meta.env.PROD: this repo's root .env sets
// NODE_ENV=development, which forces PROD=false even during `vite build`, so PROD
// would dead-code-eliminate LogRocket from production. MODE comes from the build
// command (`vite build` -> 'production', `vite` dev -> 'development') and is not
// affected by NODE_ENV.
const RECORDING_ENABLED = import.meta.env.MODE === 'production';

// Module-level guard so init runs at most once across StrictMode double-invokes
// and component re-mounts.
let initialized = false;

function initLogRocket() {
  if (initialized || !RECORDING_ENABLED) return;
  initialized = true;
  // init enables session recording plus automatic capture of unhandled JS
  // errors and console.error.
  LogRocket.init(LOGROCKET_APP_ID);
}

/**
 * Error boundary that forwards React render-time errors to LogRocket so they are
 * visible alongside the automatically-captured runtime/console errors. Children
 * are still rendered after reporting — we only report, we don't replace the UI.
 */
class LogRocketErrorBoundary extends Component<{ children: ReactNode }> {
  override componentDidCatch(error: Error, info: ErrorInfo) {
    if (RECORDING_ENABLED) {
      LogRocket.captureException(error, {
        extra: { componentStack: info.componentStack ?? '' },
      });
    }
  }

  override render() {
    return this.props.children;
  }
}

/**
 * Owns LogRocket setup: production-only init (session recording + error
 * tracking), user identification via the auth session, and a render-time error
 * boundary. Wrap the routed app with this so identify has session access and the
 * boundary catches route render errors.
 */
export function LogRocketProvider({ children }: { children: ReactNode }) {
  const { user } = useCurrentUser();

  useEffect(() => {
    initLogRocket();
  }, []);

  useEffect(() => {
    if (!RECORDING_ENABLED || !user) return;
    LogRocket.identify(user.id, {
      name: user.name,
      email: user.email,
      role: user.role,
    });
    // `useCurrentUser` rebuilds the user object each render, so key on the
    // primitive fields to avoid re-identifying on every render.
  }, [user?.id, user?.name, user?.email, user?.role]);

  return (
    <div data-testid="logrocket-provider" className="contents">
      <LogRocketErrorBoundary>{children}</LogRocketErrorBoundary>
    </div>
  );
}
